import { ref, computed, watch } from "vue"
import { defineStore, acceptHMRUpdate } from "pinia"
import { api, crowdinapi, lang2code, fixlocales } from "@/compositions/utils.ts"
import { useRoute } from "vue-router"
import { TZDate } from "@date-fns/tz"
import { format, lastDayOfMonth } from "date-fns"

export const useQueryStore = defineStore("QueryStore", () => {
  const assignedby = localStorage.getItem("babble/assignedby") ? localStorage.getItem("babble/assignedby")!.split(",") : ["bzadik", "salim", "Delena"]
  const statuses = localStorage.getItem("babble/statuses") ? localStorage.getItem("babble/statuses")!.split(",") : ["Open", "Finished", "Delivered"]
  const orderByDefault = localStorage.getItem("babble/sortOrder") || "-lastupdated"

  const query = ref<any>({
    assignedby: assignedby,
    search: "",
    status: statuses,
    client: [],
    to: "",
    from: "",
    orderBy: orderByDefault,
  })
  const projects = ref<any>([])
  const translators = ref<any>({})
  const developers = ref<any>({})
  const totalResults = ref<number | null>(null)
  const token = ref<number | null>(null)
  const loading = ref(false)
  const loadLazyTimeout = ref<any>()
  const cardSize = ref<number>(parseInt(localStorage.getItem("babble/cardSize") || "48"))
  const layout = ref(localStorage.getItem("babble/layout") || "grid")
  const timers = ref<any[]>([])
  const filteredClients = ref((localStorage.getItem("babble/filteredClients") || "").split("||").filter((n) => n))
  const showContinuous = ref((localStorage.getItem("babble/continuous") || "true") == "true")
  const lastMovement = ref(Date.now())

  const numResults = computed(() => projects.value.length)
  const projectmap = computed(() => {
    const jobs: Record<string, number> = {}
    for (var key in projects.value) {
      jobs[projects.value[key].jobid] = parseInt(key)
    }
    return jobs
  })
  const openJobs = computed(() => {
    return projects.value.filter((p: any) => p.status == "Open").map((p: any) => parseInt(p.jobid))
  })
  const filteredJobs = computed(() => {
    let filt = filteredClients.value.length > 0 ? projects.value.filter((item: any) => filteredClients.value.includes(item.client)) : projects.value
    return showContinuous.value ? filt : filt.filter((item: any) => !item.projectname.includes("Continuous"))
  })

  const clients = computed(() => (numResults.value > 0 ? [...new Set(projects.value.map((c: any) => c.client).sort())] : []))

  const abortController = ref<AbortController>()
  const route = useRoute()
  watch(
    () => route.name, // React to changes in router view
    (newRoute, oldRoute) => {
      if (oldRoute == "home" && newRoute != "home") {
        console.log(`Changing route from ${String(oldRoute || "/")} to ${String(newRoute)}`)
        if (abortController.value && newRoute != "home") abortController.value.abort("Cancel fetch for " + String(newRoute))
        clearTimers()
      }
    }
  )

  const queueBusy = ref(false)

  async function load(offset = 0) {
    // don't reload if not active app
    if (route.name != "home") return
    console.log("load", query.value, offset)
    if (offset == 0) {
      clearTimers()
      // Listeners
      document.addEventListener("visibilitychange", updateDashboardTab)
      document.addEventListener("mousemove", resetActivity)
      document.addEventListener("keydown", resetActivity)

      let interval1 = setTimeout(load, 30 * 60000)
      timers.value.push(interval1)

      projects.value = []
      if (abortController.value?.signal) {
        abortController.value.abort("Cancel fetch because reloading")
      }
      abortController.value = new AbortController()
    }
    const answer = await api("getProjects", "POST", query.value, offset, abortController.value!.signal)
    console.log("answer", answer)
    if (answer && answer.length > 0) {
      token.value = Date.now()
      // console.log(token.value)
      if (answer[0].count != undefined) {
        totalResults.value = answer[0].count
      }
      if (totalResults.value! > 0) {
        projects.value.push(...answer)
        let jobIds = answer.map((p: any) => p.jobid)
        details(jobIds)
      }
    }

    return answer
  }

  // Continues loading if there is more, but limits for "Complete" to fill the page
  async function lazyLoad(more?: boolean) {
    loading.value = true
    let offset = more === true && numResults.value != undefined ? numResults.value : 0
    await load(offset)
    if (!query.value.status.includes("Complete")) {
      loadMore()
    }
    loading.value = false
  }

  async function loadMore() {
    if (loadLazyTimeout.value) {
      clearTimeout(loadLazyTimeout.value)
    }
    console.log(route.name, numResults.value, totalResults.value)
    if (route.name == "home" && numResults.value < totalResults.value!) {
      loadLazyTimeout.value = setTimeout(lazyLoad, 300, true)
    }
  }

  async function clearTimers() {
    timers.value.forEach((t) => clearInterval(t))
    document.removeEventListener("visibilitychange", updateDashboardTab)
    document.removeEventListener("mousemove", resetActivity)
    document.removeEventListener("keydown", resetActivity)
    lastMovement.value = Date.now()
  }

  async function getJob(id: any, index: number | null = null) {
    let job = await api("getJob", "POST", { jobid: id })
    if (index != null) {
      projects.value[index] = job
      await details([id])
    }
    return job
  }

  async function details(jobIds: any) {
    const jobs = await api("getProjectDetails", "POST", { jobs: jobIds }, 0, abortController.value!.signal)
    if (jobs && jobs.length) {
      jobs.forEach((j: any) => {
        let index = projectmap.value[j.jobid]
        let code = lang2code[j.language.split("->")[0]]
        projects.value[index].translators = projects.value[index].translators || {}
        projects.value[index].translators[code] = j
        projects.value[index].translators[code].code = code
        projects.value[index].translators[code]["index"] = index
        translators.value[j.userid] = j
      })
      if (numResults.value == totalResults.value || query.value.status.includes("Complete")) {
        getDevelopers()
      }
    }
  }

  async function interval(jobIds: any) {
    clearTimers()
    if (!document.hidden) {
      details(jobIds)
    }
    let interval1 = setTimeout(interval, 30 * 60000, jobIds)
    timers.value.push(interval1)
  }

  async function getClientList(devs: any) {
    return await api("getClientList", "POST", { devs: devs })
  }

  async function getClient(client: any) {
    return await api("getClient", "POST", { client: client })
  }

  async function getTeam(client: any) {
    return await api("getTeam", "POST", {
      client: client,
    })
  }

  async function updateJobStatus(t: any, status: string | null = null) {
    const updated = await api("updateJobStatus", "POST", { id: t.ID, status: status })
    return updated.status
  }

  async function resetStatuses(newstatus: string, index: number) {
    console.log(newstatus)
    for (let [code, values] of Object.entries(projects.value[index].translators) as [string, any][]) {
      let status = await updateJobStatus(values, newstatus)
      projects.value[index].translators[code].status = newstatus
    }
    await updateField("status", "Open", projects.value[index].jobid)
    await insertActivity(projects.value[index].projectname, "Open", projects.value[index].jobid)
  }

  async function getUserDetails(u: any) {
    return await api("getUserDetails", "POST", { username: u })
  }

  async function getStats(jobid: any, crowdin: any) {
    return await api("crowdin", "POST", {
      crowdin: crowdin,
      languagecode: "es-MX",
      request: "wordsbylanguage",
    })
  }

  async function getT(user: any, translators: any) {
    Object.values(translators).forEach((t: any) => {
      if (t.userid == user) {
        return t.code
      }
    })
  }

  async function getDevelopers() {
    let devClients = projects.value.reduce((devs: string[], p: any) => {
      if (!developers.value[p.client] && !devs.includes(p.client) && p.linktofile && p.linktofile.substring(0, 1) == "[") devs.push(p.client)
      return devs
    }, [] as string[])

    if (devClients.length) {
      const devlist = await api("getDevelopers", "POST", {
        developers: devClients,
      })
      if (devlist && devlist[0]) {
        devlist.forEach((d: any) => {
          developers.value[d.client] = d
        })
      }
    }
  }

  async function updateRating(translator: any, rating: any, review: any, jobid: any) {
    const result = await api("updateRating", "POST", {
      translator: translator,
      rating: rating,
      notes: review,
      user: localStorage.getItem("babble/pm"),
    })
    translators.value[translator].ratings = result.ratings
    translators.value[translator].notes = review
  }

  async function updateField(field: string, value: any, jobid: any, table: string = "projects", update: boolean = false) {
    const response = await api("updateField", "POST", {
      field: field,
      value: value,
      id: jobid,
      table: table,
    })
    if (update == true) projects.value[projectmap.value[jobid]][field] = value
  }

  async function getJobActivity(jobid: any) {
    return await api("getJobActivity", "POST", {
      jobid: jobid,
    })
  }

  async function insertActivity(project: string = "", activity: string = "", jobid: any = "", language: string = "", user: string | null = localStorage.getItem("babble/pm")) {
    return await api("insertActivity", "POST", {
      project: project,
      activity: activity,
      jobid: jobid,
      language: language,
      user: user,
    })
  }

  async function deleteActivity(activity: string = "", jobid: any = "", user: string | null = localStorage.getItem("babble/pm")) {
    return await api("deleteActivity", "POST", {
      activity: activity,
      jobid: jobid,
      user: user,
    })
  }

  async function getSupplementalCandidates(client: any) {
    return await api("getSupplementalCandidates", "POST", {
      client: client,
    })
  }

  async function markSupplemental(invoicetochange: any, master: any, customer_id: any) {
    let method = customer_id ? "markMonthlySupplemental" : "markSupplemental"
    return await api(method, "POST", {
      invoicetochange: invoicetochange,
      master: master,
    })
  }

  async function getRequest(customerinvoice: any) {
    return await api("getRequest", "POST", {
      customerinvoice: customerinvoice,
    })
  }

  async function getTaskList(entry: any) {
    return await api("getTaskList", "POST", {
      entry: entry,
    })
  }

  async function getSlackTaskList(entry: any, customerinvoice: any) {
    return await api("getSlackTaskList", "POST", {
      entry: entry,
      customerinvoice: customerinvoice,
    })
  }

  async function updateStripeInvoice(customer_id: any, customerinvoice: any, lineitems: any, test: boolean = import.meta.env.VITE_TEST_STRIPE === "true") {
    return await api("updateStripeInvoice", "POST", {
      customer_id: customer_id,
      customerinvoice: customerinvoice,
      lineitems: lineitems,
      test: test,
    })
  }

  async function postSlack(blocks: any) {
    return await api("postSlack", "POST", {
      blocks: blocks,
    })
  }

  async function getProjectLanguages(project_id: any) {
    return await crowdinapi("getProjectLanguages", {
      project_id: project_id,
    })
  }

  async function getCrowdinDownloadURL(project_id: any, buildId: any) {
    return await crowdinapi(
      "getCrowdinDownloadURL",
      {
        project_id: project_id,
        buildId: buildId,
      },
      0,
      abortController.value!.signal
    )
  }

  async function getClientInvoices(offset: number = 0, searchOptions?: any) {
    return await api("getClientInvoices", "POST", searchOptions, offset)
  }

  async function getInvoiceJobs(id: any) {
    return await api("getInvoiceJobs", "POST", {
      customerinvoice: id,
    })
  }

  async function updateCrowdinTask(project_id: any, task_id: any, field: string, value: any) {
    return await crowdinapi("updateTask", { project_id: project_id, task_id: task_id, field: field, value: value })
  }
  async function getLanguageProgress(index: number, force?: boolean) {
    if (!projects.value[index] || !projects.value[index].translators || (projects.value[index].status != "Open" && projects.value[index].status != "Finished" && force != true))
      return

    // if pressing the reload button, update job statuses too
    if (force) details([projects.value[index].jobid])

    if (projects.value[index].linktofile.substring(0, 1) == "[") {
      // keep crowdin from overloading
      if (queueBusy.value === true) {
        setTimeout(() => getLanguageProgress(index, force), 1500)
        return
      }

      queueBusy.value = true
      const isContinuous = projects.value[index].projectname.includes("Continuous") && ["Open", "Finished"].includes(projects.value[index].status)
      const codes = Object.keys(projects.value[index].translators)
      if (codes == undefined || codes.length == 0) return
      if (!abortController.value?.signal) abortController.value = new AbortController()
      const data = await crowdinapi(
        "wordsbyjob",
        {
          crowdin: projects.value[index].linktofile,
          languages: codes,
        },
        0,
        abortController.value!.signal
      )
      queueBusy.value = false

      for (const [lang, stats] of Object.entries(data) as [string, any][]) {
        if (!projects.value[index]) return // if query changes, these slow calls to Crowdin can be delayed and useless
        let code = fixlocales(lang, codes)
        if (!codes.includes(code)) {
          if (lang != "error")
            console.log("Wordcount fail. Can't match " + lang + " for " + JSON.stringify(codes) + " " + projects.value[index].projectname, Object.keys(data).join(","), data)
          continue
        }
        projects.value[index].translators[code].stats = stats
        // add on the last known word count as 'translated' to get the right info for TranslatorList
        if (isContinuous) projects.value[index].translators[code].stats.translated = parseInt(projects.value[index].translators[code].wordcount)
      }
      projects.value[index].crowdinLoaded = new Date().toLocaleString().split(" ")[1]

      if (isContinuous) updateWordcount(index)
      return data
    }
  }

  async function updateWordcount(index: number) {
    const codes = Object.keys(projects.value[index].translators)
    let startdate = new TZDate(projects.value[index].created, "Asia/Tokyo").toISOString().substring(0, 7) + "-01"
    let enddate = format(lastDayOfMonth(new TZDate(startdate, "Asia/Tokyo")), "yyyy-MM-dd")

    if (codes == undefined || codes.length == 0 || new Date().toISOString().substring(0, 7) != startdate) return

    let crowdinIds = Object.values(projects.value[index].translators).map((t: any) => t.crowdin)

    const response = await crowdinapi(
      "continuousWordCounts",
      {
        crowdin: projects.value[index].linktofile,
        languages: codes,
        crowdinIds: crowdinIds || [],
        dateFrom: startdate,
        dateTo: enddate,
      },
      0,
      abortController.value!.signal
    )
    if (response && !response.error) {
      for (const [locale, continuousStats] of Object.entries(response) as [string, any][]) {
        let code = fixlocales(locale, codes)
        if (!codes.includes(code)) {
          if (locale != "error")
            console.log(
              "Continuous update error. Can't match " + locale + " for " + JSON.stringify(codes) + " " + projects.value[index].projectname,
              Object.keys(response).join(","),
              response
            )
          continue
        }
        const stats = projects.value[index].translators[code].stats
        stats.translated = continuousStats.translated
        stats.reviewed = continuousStats.reviewed ?? 0
        stats.words = continuousStats.translated + (stats.untranslated ?? 0)

        let wordcount = `${continuousStats.translated} words`
        let reviewcount = `${continuousStats.reviewed ?? 0} words`

        if (projects.value[index].translators[code].wordcount != wordcount) {
          console.log(`${locale}/${code} ${wordcount}  ${projects.value[index].translators[code].ID}`)
          projects.value[index].translators[code].wordcount = wordcount
          updateField("wordcount", wordcount, projects.value[index].translators[code].ID, "joblog")
        }
        if (projects.value[index].translators[code].reviewcount != reviewcount) {
          projects.value[index].translators[code].reviewcount = reviewcount
          updateField("reviewcount", reviewcount, projects.value[index].translators[code].ID, "joblog")
        }
      }
      if (projects.value[index]) projects.value[index].crowdinLoaded = new Date().toLocaleString().split(" ")[1]
    }
  }

  async function updateDashboardTab() {
    // check if it's been more than a 30 minutes, update, more than 1 hour reload
    // console.log("Home visibility change")
    if (document.hidden || route.name != "home" || Date.now() - lastMovement.value < 30 * 60 * 1000) return
    if (Date.now() - lastMovement.value > 60 * 60 * 1000) {
      console.log("reloading projects")
      load()
    } else if (projects.value.length && openJobs.value.length > 0) {
      console.log("updating project details")
      details(openJobs.value)
    }
  }

  function resetActivity() {
    lastMovement.value = Date.now()
  }

  return {
    query,
    crowdinapi,
    projects,
    loading,
    translators,
    developers,
    projectmap,
    numResults,
    clients,
    filteredClients,
    filteredJobs,
    showContinuous,
    totalResults,
    token,
    cardSize,
    layout,
    load,
    lazyLoad,
    loadMore,
    updateJobStatus,
    getUserDetails,
    getClientList,
    getClient,
    getRequest,
    getTaskList,
    getSlackTaskList,
    getTeam,
    getDevelopers,
    getStats,
    updateRating,
    updateField,
    resetStatuses,
    getJobActivity,
    insertActivity,
    deleteActivity,
    getSupplementalCandidates,
    markSupplemental,
    updateStripeInvoice,
    details,
    getLanguageProgress,
    postSlack,
    updateCrowdinTask,
    getProjectLanguages,
    getCrowdinDownloadURL,
    getJob,
    getClientInvoices,
    getInvoiceJobs,
  }
})

// hot updates
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useQueryStore, import.meta.hot))
}
